import axios from 'axios';
import {
  API_CONFIG,
  buildTenantApiUrl,
  generateRequestId,
  getAuthHeaders,
} from '../config/api';

export type AgentShareApiResponse<TDetails = any> = {
  success: boolean;
  code: string;
  message: string;
  details?: TDetails;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
};

export type AgentShareProfile = {
  userId: string | number;
  nickname?: string | null;
  avatar?: string | null;
  merchantId?: string | number | null;
  merchantName?: string | null;
  merchantAddress?: string | null;
  merchantIdentity?: string | number | null;
  bio?: string | null;
  certifications?: string | null;
  languages?: string | null;
  website?: string | null;
  licenseNo?: string | null;
  /** Only a server-provided license verification result may show a badge. */
  licenseVerified?: boolean | null;
  email?: string | null;
  phone?: string | null;
  activeListingCount?: number | null;
  historyListingCount?: number | null;
};

export type AgentShareListing = {
  unitId: string | number;
  unitNo?: string | null;
  title?: string | null;
  mainImageUrl?: string | null;
  price?: string | number | null;
  bedroomCount?: number | null;
  bathroomCount?: string | number | null;
  availableFrom?: string | null;
  leaseTerm?: string | null;
  address?: string | null;
  tags?: string | null;
  availableDate?: string | null;
  description?: string | null;
  listingOriginatingType?: string | null;
  amenities?: string | null;
  lon?: string | number | null;
  lat?: string | number | null;
};

export type AgentShareListingsDetails = {
  records: AgentShareListing[];
  total: number;
  size: number;
  current: number;
  pages: number;
};

export type AgentShareListingsRequest = {
  agentId: string | number;
  page?: number;
  pageSize?: number;
};

export type AgentQrCodeImageRequest = {
  agentId: string | number;
  agentUrl: string;
};

export type BrokerageShareCardEmailRequest = {
  recipientEmail: string;
  brokerageName: string;
  brokerageCompanyName: string;
  brokerageImage: string;
  appointmentUrl: string;
  learnMoreUrl: string;
};

export type BrokerageShareCardEmailResponse = AgentShareApiResponse<string>;

export type CustomerIntakeLivingLocation = {
  source?: string | null;
  label?: string | null;
  city?: string | null;
  neighborhoodName?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radiusKm?: number | null;
};

export type CustomerIntakeCommuteDestination = {
  address?: string | null;
  buildingName?: string | null;
  institutionName?: string | null;
  lat?: number | null;
  lng?: number | null;
  name?: string | null;
  schoolBuildingId?: number | null;
};

export type CustomerIntakePetPolicy = {
  name?: string | null;
  type?: string | null;
  description?: string | null;
};

/** Exact public Tenant manual-add profile contract. */
export type CustomerIntakeProfile = {
  employmentStatus?: 'employed' | 'student' | 'self-employed' | 'unemployed' | null;
  employmentType?: string | null;
  creditScoreMin?: number | null;
  creditScoreMax?: number | null;
  incomeSource?: 'employment' | 'student_loan' | 'family' | 'savings' | 'investment' | 'other' | null;
  incomeSourceDetail?: string | null;
  guarantorStatus?: 'AVAILABLE' | 'NOT_AVAILABLE' | 'UNKNOWN' | null;
  latitude?: number | null;
  longitude?: number | null;
  radiusKm?: number | null;
  priceMin?: number | null;
  priceMax?: number | null;
  bedrooms?: number | null;
  bedroomsMode?: 'exact' | 'at_least' | null;
  bathrooms?: number | null;
  propertyTypes?: string[] | null;
  recommendationStyle?: 'value_focused' | 'commute_focused' | 'best_overall' | null;
  parking?: boolean | null;
  pets?: boolean | null;
  petPolicy?: CustomerIntakePetPolicy[] | null;
  moveInDate?: string | null;
  availableFrom?: string | null;
  availableBy?: string | null;
  moveInWindowKind?: 'fixed' | 'flexible' | 'range' | null;
  livingLocations?: CustomerIntakeLivingLocation[] | null;
  commuteDestinations?: CustomerIntakeCommuteDestination[] | null;
  commuteMethods?: string[] | null;
  maxCommuteMinutes?: number | null;
  currentLiving?: CustomerIntakeLivingLocation | null;
  primaryFactor?: string | null;
  mustHaveInUnit?: string[] | null;
  mustHaveAmenities?: string[] | null;
  residenceType?: 'individual' | 'co_sign' | 'affordable_housing' | null;
  institutionName?: string | null;
  residenceTypeDetail?: string | null;
};

export type CustomerIntakeRequest = {
  userId?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  profile?: CustomerIntakeProfile | null;
  leadSource?: 'SHARE_LINK' | 'PRIVATE_IMPORT' | null;
  leadSourceDetail?: string | null;
};

export type CustomerLifecycleBridgeResponse = {
  customerId: string | number;
  created: boolean;
};

export type CustomerIntakeResponse = AgentShareApiResponse<CustomerLifecycleBridgeResponse>;

export type AgentShareRouteProfile = {
  merchantName?: string | null;
  nickname?: string | null;
};

export type AgentShareRequestOptions = { suppressGlobalNotification?: boolean };

export const slugifyAgentSharePathPart = (value: string | null | undefined): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9]/g, '');

export const buildAgentSharePathKeyword = (profile: AgentShareRouteProfile): string => {
  const nicknameSlug = slugifyAgentSharePathPart(profile?.nickname);

  if (!nicknameSlug) {
    throw new Error('nickname is required');
  }

  return nicknameSlug;
};

export const buildAgentSharePagePath = (profile: AgentShareRouteProfile): string =>
  `/agent/${buildAgentSharePathKeyword(profile)}`;

export const buildAgentSharePageUrl = (
  profile: AgentShareRouteProfile,
  origin: string = window.location.origin
): string => buildAgentAppointmentPageUrl(buildAgentSharePagePath(profile), origin);

export const buildAgentAppointmentPageUrl = (
  path: string,
  origin: string = window.location.origin
): string => `${resolveAgentShareOrigin(origin)}${path}`;

export const buildCustomerIntakePagePath = (profile: AgentShareRouteProfile): string =>
  `/customer-intake/${buildAgentSharePathKeyword(profile)}`;

export const buildCustomerIntakePageUrl = (
  profile: AgentShareRouteProfile,
  origin: string = window.location.origin
): string => `${resolveAgentShareOrigin(origin)}${buildCustomerIntakePagePath(profile)}`;

const resolveAgentShareOrigin = (origin: string): string => {
  try {
    const hostname = new URL(origin).hostname;
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' ||
      hostname === 'stage.allroomie.com'
    ) {
      return 'https://stage.allroomie.com';
    }
  } catch {
    // Keep the supplied origin if it cannot be parsed as a URL.
  }

  return origin.replace(/\/$/, '');
};

export const buildAgentOgShareUrl = (
  profile: AgentShareRouteProfile,
  origin: string = window.location.origin
): string => {
  const merchantSlug = slugifyAgentSharePathPart(profile?.merchantName);
  const nicknameSlug = buildAgentSharePathKeyword(profile);
  // The legacy social-preview endpoint still resolves merchant + nickname.
  if (!merchantSlug) return buildAgentSharePageUrl(profile, origin);
  const pathKeyword = `${merchantSlug}-${nicknameSlug}`;
  return `${resolveAgentShareOrigin(origin)}/api/v1/tenant/public/share/agent/${encodeURIComponent(pathKeyword)}`;
};

const requireValue = (value: string | number | null | undefined, fieldName: string): string => {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    throw new Error(`${fieldName} is required`);
  }
  return normalized;
};

class BsideAgentShareService {
  /**
   * Public share page lookup:
   * GET /public/agents/by-slug/{nicknameSlug}/profilev1
   * Also accepts existing merchantSlug-nicknameSlug links.
   */
  async getAgentProfileByPathKeywordRaw(
    pathKeyword: string,
    options: AgentShareRequestOptions = {}
  ): Promise<AgentShareApiResponse<AgentShareProfile>> {
    const slug = requireValue(pathKeyword, 'pathKeyword');
    const url = buildTenantApiUrl(
      `/public/agents/by-slug/${encodeURIComponent(slug)}/profilev1`
    );

    const response = await axios.get<AgentShareApiResponse<AgentShareProfile>>(url, {
      headers: {
        ...API_CONFIG.HEADERS,
        ...(options.suppressGlobalNotification ? { 'X-Suppress-Global-Notification': 'true' } : {}),
      },
      timeout: API_CONFIG.TIMEOUT,
    });

    return response.data;
  }

  async getAgentProfileByPathKeyword(pathKeyword: string, options: AgentShareRequestOptions = {}): Promise<AgentShareProfile> {
    const data = await this.getAgentProfileByPathKeywordRaw(pathKeyword, options);
    if (!data?.success || !data.details) {
      throw new Error(data?.message || 'Failed to load agent profile');
    }
    return data.details;
  }

  /**
   * Public direct profile lookup:
   * GET /public/agents/{agentId}/profile
   */
  async getAgentProfileRaw(
    agentId: string | number,
    options: AgentShareRequestOptions = {}
  ): Promise<AgentShareApiResponse<AgentShareProfile>> {
    const id = requireValue(agentId, 'agentId');
    const url = buildTenantApiUrl(`/public/agents/${encodeURIComponent(id)}/profile`);

    const response = await axios.get<AgentShareApiResponse<AgentShareProfile>>(url, {
      headers: {
        ...API_CONFIG.HEADERS,
        ...(options.suppressGlobalNotification ? { 'X-Suppress-Global-Notification': 'true' } : {}),
      },
      timeout: API_CONFIG.TIMEOUT,
    });

    return response.data;
  }

  async getAgentProfile(agentId: string | number, options: AgentShareRequestOptions = {}): Promise<AgentShareProfile> {
    const data = await this.getAgentProfileRaw(agentId, options);
    if (!data?.success || !data.details) {
      throw new Error(data?.message || 'Failed to load agent profile');
    }
    return data.details;
  }

  /**
   * Public listing cards owned by the agent:
   * GET /public/agents/{agentId}/listings
   */
  async getAgentListingsRaw(
    params: AgentShareListingsRequest
  ): Promise<AgentShareApiResponse<AgentShareListingsDetails>> {
    const id = requireValue(params?.agentId, 'agentId');
    const url = buildTenantApiUrl(`/public/agents/${encodeURIComponent(id)}/listings`, {
      page: params.page || 1,
      pageSize: params.pageSize || 20,
    });

    const response = await axios.get<AgentShareApiResponse<AgentShareListingsDetails>>(url, {
      headers: {
        ...API_CONFIG.HEADERS,
      },
      timeout: API_CONFIG.TIMEOUT,
    });

    return response.data;
  }

  async getAgentListings(params: AgentShareListingsRequest): Promise<AgentShareListingsDetails> {
    const data = await this.getAgentListingsRaw(params);
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to load agent listings');
    }
    return data.details || {
      records: [],
      total: 0,
      size: params.pageSize || 20,
      current: params.page || 1,
      pages: 0,
    };
  }

  /**
   * Public history listing cards owned by the agent:
   * GET /public/agents/{agentId}/listings/history
   */
  async getAgentHistoryListingsRaw(
    params: AgentShareListingsRequest
  ): Promise<AgentShareApiResponse<AgentShareListingsDetails>> {
    const id = requireValue(params?.agentId, 'agentId');
    const url = buildTenantApiUrl(`/public/agents/${encodeURIComponent(id)}/listings/history`, {
      page: params.page || 1,
      pageSize: params.pageSize || 20,
    });

    const response = await axios.get<AgentShareApiResponse<AgentShareListingsDetails>>(url, {
      headers: {
        ...API_CONFIG.HEADERS,
      },
      timeout: API_CONFIG.TIMEOUT,
    });

    return response.data;
  }

  async getAgentHistoryListings(
    params: AgentShareListingsRequest
  ): Promise<AgentShareListingsDetails> {
    const data = await this.getAgentHistoryListingsRaw(params);
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to load agent history listings');
    }
    return data.details || {
      records: [],
      total: 0,
      size: params.pageSize || 20,
      current: params.page || 1,
      pages: 0,
    };
  }

  /**
   * Public agent QR code image URL.
   *
   * The Apifox title mentions an agent share card, but the currently wired
   * backend method returns the QR image itself. The B-side name card UI owns
   * the surrounding card layout on the frontend.
   */
  buildAgentQrCodeImageUrl(req: AgentQrCodeImageRequest): string {
    const agentId = requireValue(req?.agentId, 'agentId');
    const agentUrl = requireValue(req?.agentUrl, 'agentUrl');
    return buildTenantApiUrl('/public/share/agent/qrcode', {
      agentId,
      agentUrl,
    });
  }

  /** Legacy alias for earlier backend-owned card experiments. */
  buildAgentNameCardImageUrl(req: AgentQrCodeImageRequest): string {
    return this.buildAgentQrCodeImageUrl(req);
  }

  /**
   * Public agent QR code image fetch:
   * GET /public/share/agent/qrcode?agentId=&agentUrl=
   */
  async getAgentQrCodeImage(req: AgentQrCodeImageRequest): Promise<Blob> {
    const url = this.buildAgentQrCodeImageUrl(req);
    const response = await axios.get<Blob>(url, {
      headers: {
        Accept: 'image/jpeg',
      },
      responseType: 'blob',
      timeout: API_CONFIG.TIMEOUT,
    });

    return response.data;
  }

  /** Legacy alias for earlier backend-owned card experiments. */
  async getAgentNameCardImage(req: AgentQrCodeImageRequest): Promise<Blob> {
    return this.getAgentQrCodeImage(req);
  }

  /**
   * Public renter intake submission:
   * POST /public/agents/{agentId}/customers/manual-add
   *
   * This endpoint is anonymous. Keep the ResultInfo envelope intact so callers
   * can handle backend business failures and request metadata themselves.
   */
  async manualAddCustomer(
    agentId: string | number,
    request: CustomerIntakeRequest,
    options: AgentShareRequestOptions = {}
  ): Promise<CustomerIntakeResponse> {
    const id = requireValue(agentId, 'agentId');
    const url = buildTenantApiUrl(
      `/public/agents/${encodeURIComponent(id)}/customers/manual-add`
    );

    const response = await axios.post<CustomerIntakeResponse>(url, request, {
      headers: {
        ...API_CONFIG.HEADERS,
        RequestId: generateRequestId(),
        ...(options.suppressGlobalNotification ? { 'X-Suppress-Global-Notification': 'true' } : {}),
      },
      timeout: API_CONFIG.TIMEOUT,
    });

    return response.data;
  }

  /**
   * Share a brokerage card through the backend-generated email template.
   * POST /api/v1/tenant/tenant/mail/share-brokerage-card
   */
  async shareBrokerageCardByEmail(
    request: BrokerageShareCardEmailRequest
  ): Promise<BrokerageShareCardEmailResponse> {
    // BrokerageShareCardVO requires all six fields, including the agent's company and photo.
    const fields: Array<[keyof BrokerageShareCardEmailRequest, string]> = [
      ['recipientEmail', 'recipient email'], ['brokerageName', 'agent name'],
      ['brokerageCompanyName', 'brokerage name'], ['brokerageImage', 'profile photo'],
      ['appointmentUrl', 'booking link'], ['learnMoreUrl', 'profile link'],
    ];
    const missing = fields.filter(([key]) => !request[key]?.trim()).map(([, label]) => label);
    if (missing.length) throw new Error(`Complete the ${missing.join(', ')} before sending this email.`);
    try {
      const url = buildTenantApiUrl(API_CONFIG.ENDPOINTS.TENANT_SHARE_BROKERAGE_CARD_EMAIL);
      const response = await axios.post<BrokerageShareCardEmailResponse>(url, request, {
        headers: {
          ...API_CONFIG.HEADERS,
          ...getAuthHeaders(),
        },
        timeout: API_CONFIG.TIMEOUT,
      });

      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to share brokerage card by email');
      }

      return response.data;
    } catch (error) {
      console.error('Failed to share brokerage card by email:', error);
      throw error;
    }
  }

}

const bsideAgentShareService = new BsideAgentShareService();
export default bsideAgentShareService;
